// /frontend/src/pages/dashboard/post/linkedin/LinkedInPostService.js
import axios from 'axios';

/**
 * publishLinkedInPost
 * Sends a request to your backend to publish a post on a connected LinkedIn account.
 *
 * @param {string} linkedinId - The LinkedIn profile or organization ID/URN to post on.
 * @param {string} message - The text/caption.
 * @param {string} [mediaUrl] - (Optional) Public URL for an image (or video).
 * @returns {Promise<Object>} - Resolves with JSON from the server (success or error).
 */
export async function publishLinkedInPost(linkedinId, message, mediaUrl) {
  try {
    // Basic validations
    if (!linkedinId) {
      throw new Error('LinkedIn ID is required');
    }
    if (!message && !mediaUrl) {
      throw new Error('You must provide either a message or media');
    }
    
    // POST to your Node backend route, with user session cookies
    const res = await axios.post(
      '/api/posting/linkedin/publish',
      { linkedinId, message, mediaUrl },
      {
        withCredentials: true, // necessary for session-based auth
      }
    );
    
    // On success, res.data might be { success: true, post_urn, message } or similar
    console.log('publishLinkedInPost success:', res.data);
    return res.data;
    
  } catch (err) {
    console.error('publishLinkedInPost error:', err);
    
    // Extract the actual error message from various possible locations
    let errorMessage = 'Failed to post to LinkedIn';
    
    // If this is our custom validation error, preserve it
    if (!err.response && err.message) {
      errorMessage = err.message;
    } else if (err.response?.data) {
      // Check multiple possible error locations in order of preference
      if (err.response.data.error && typeof err.response.data.error === 'string') {
        errorMessage = err.response.data.error;
      } else if (err.response.data.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (err.response.data.details) {
        errorMessage = err.response.data.details;
      } else if (err.response.data.serviceErrorCode) {
        // LinkedIn specific error handling
        errorMessage = getLinkedInErrorByCode(err.response.data.serviceErrorCode, err.response.data.message);
      } else if (err.response.data.status && err.response.data.status !== 200) {
        // LinkedIn API v2 error format
        if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (typeof err.response.data === 'string') {
        errorMessage = err.response.data;
      }
    } else if (err.response?.status) {
      // Handle HTTP status codes
      errorMessage = getLinkedInErrorByStatus(err.response.status, err.message);
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    // Throw error with the extracted message
    throw new Error(errorMessage);
  }
}

/**
 * Maps LinkedIn error codes to user-friendly messages
 */
function getLinkedInErrorByCode(code, fallbackMessage) {
  const errorMap = {
    65600: 'LinkedIn access token expired. Please reconnect your account.',
    65601: 'LinkedIn token revoked. Please reconnect your account.',
    100: 'LinkedIn unpermitted access. Please reconnect with proper permissions.',
    101: 'LinkedIn permission required. Please reconnect with posting permissions.',
    65604: 'LinkedIn application suspended. Please contact support.',
    42: 'LinkedIn rate limit exceeded. Please wait before posting again.',
    40001: 'LinkedIn unauthorized. Please reconnect your account.',
    40101: 'LinkedIn member not found. Account may be restricted.',
    40301: 'LinkedIn forbidden. Check your account permissions.',
    42901: 'LinkedIn quota exceeded. Daily posting limit reached.',
    50001: 'LinkedIn server error. Please try again later.',
    403: 'LinkedIn access denied. Check your permissions.',
    429: 'Too many requests. Please wait a moment before trying again.',
  };
  
  return errorMap[code] || fallbackMessage || `LinkedIn error (code: ${code})`;
}

/**
 * Maps HTTP status codes to user-friendly messages
 */
function getLinkedInErrorByStatus(status, fallbackMessage) {
  const statusMap = {
    400: 'Invalid LinkedIn request. Please check your content.',
    401: 'LinkedIn authentication failed. Please reconnect your account.',
    403: 'LinkedIn permission denied. Check your account permissions.',
    404: 'LinkedIn resource not found. Account may be deleted.',
    422: 'LinkedIn validation error. Check your content format.',
    429: 'LinkedIn rate limit exceeded. Please try again later.',
    500: 'LinkedIn server error. Please try again later.',
    502: 'LinkedIn server error. Please try again later.',
    503: 'LinkedIn service unavailable. Please try again later.',
  };
  
  return statusMap[status] || fallbackMessage || `LinkedIn error (status: ${status})`;
}

/**
 * Helper function to get user-friendly error messages
 * Can be used by UI components to show better error dialogs
 */
export function getLinkedInErrorMessage(error) {
  const message = error.message || error.toString();
  const lowerMessage = message.toLowerCase();
  
  // Map technical errors to user-friendly messages with actions
  if (lowerMessage.includes('token') || lowerMessage.includes('expired') || lowerMessage.includes('revoked')) {
    return {
      title: 'Connection Expired',
      message: 'Your LinkedIn connection has expired. Please reconnect your account in Settings.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('permission') || lowerMessage.includes('unpermitted')) {
    return {
      title: 'Permission Required',
      message: 'Missing LinkedIn permissions. Please reconnect with all required permissions.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('unauthorized')) {
    return {
      title: 'Authentication Failed',
      message: 'LinkedIn authentication failed. Please reconnect your account.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('rate limit') || lowerMessage.includes('too many')) {
    return {
      title: 'Rate Limit Reached',
      message: 'You\'ve reached LinkedIn\'s posting limit. Please wait a few minutes before trying again.',
      action: 'wait'
    };
  }
  
  if (lowerMessage.includes('quota exceeded') || lowerMessage.includes('daily posting limit')) {
    return {
      title: 'Daily Limit Reached',
      message: 'You\'ve reached your daily LinkedIn posting limit. Try again tomorrow.',
      action: 'wait_daily'
    };
  }
  
  if (lowerMessage.includes('member not found') || lowerMessage.includes('resource not found')) {
    return {
      title: 'Account Not Found',
      message: 'LinkedIn account not found or may be restricted. Please check your account status.',
      action: 'check_account'
    };
  }
  
  if (lowerMessage.includes('suspended')) {
    return {
      title: 'Application Suspended',
      message: 'The LinkedIn application has been suspended. Please contact support.',
      action: 'contact_support'
    };
  }
  
  if (lowerMessage.includes('validation error')) {
    return {
      title: 'Invalid Content',
      message: 'Your content doesn\'t meet LinkedIn\'s requirements. Please check the format.',
      action: 'edit_content'
    };
  }
  
  if (lowerMessage.includes('server error')) {
    return {
      title: 'Server Error',
      message: 'LinkedIn is experiencing issues. Please try again later.',
      action: 'retry'
    };
  }
  
  // Default
  return {
    title: 'Posting Failed',
    message: message,
    action: null
  };
}